import * as React from 'react';
import { Alert, Switch } from '@ixicare/ui';
import { ApiKeyType, IntegrationSlug, ProfileType } from '@ixicare/resources';
import { observer } from 'mobx-react';
import { useStores } from '~store/utils';
import { t } from '~i18n';
import { SectionDivider, SectionTitle } from '~components/Utils/Section';
import { useSiteIntegration } from '../hooks/useSiteIntegrations';
import { useSiteApiKeys } from '../hooks/useSiteApiKeys';

/**
 * Switches the Websocket integration of a site on or off. Clients authenticate with a key of type
 * Websocket, managed in the key list; this only decides whether the site accepts them. Its settings
 * (forward beacons) are configured by Ixicare, so only employees see them.
 */
export const SiteWebsocketSetting = observer(({ siteUuid }: { siteUuid: string }) => {
  const { auth } = useStores();
  const isEmployee = auth.userType === ProfileType.WatcherrEmployee;
  const { integration, isLoading, update } = useSiteIntegration(siteUuid, IntegrationSlug.WEBSOCKET);
  const { data: apiKeys } = useSiteApiKeys(siteUuid);

  const isEnabled = !!integration?.isEnabled;
  const metaInfo = integration?.metaInfo ?? {};
  const forwardBeacons = !!metaInfo.forward_beacons;
  const hasValidKey = !!apiKeys?.some((apiKey) => apiKey.keyType === ApiKeyType.WEBSOCKET && !apiKey.isExpired);
  const busy = isLoading || update.isPending;

  return (
    <>
      <SectionDivider />
      <SectionTitle>{t('site.developer.websocket.title')}</SectionTitle>
      <div className="row">
        <div className="col-12 col-md-6">
          <Switch
            label={t('site.developer.websocket.enabled')}
            checked={isEnabled}
            disabled={busy}
            onChange={(checked) => update.mutate({ isEnabled: checked, metaInfo })}
          />
          {isEmployee && (
            <Switch
              label={t('site.developer.websocket.forwardBeacons')}
              checked={forwardBeacons}
              disabled={busy || !isEnabled}
              onChange={(checked) => update.mutate({ isEnabled, metaInfo: { ...metaInfo, forward_beacons: checked } })}
            />
          )}
          {isEnabled && apiKeys && !hasValidKey && <Alert type="warning">{t('site.developer.websocket.noKey')}</Alert>}
        </div>
        <div className="col-12 col-md-6">
          <Alert type="message">{t('site.developer.websocket.description')}</Alert>
        </div>
      </div>
    </>
  );
});
